import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadFilingPdf } from '../../services/filingService';
import JurisdictionRow from './JurisdictionRow';
import './FilingCard.css';

/**
 * Filing Card Component
 * Displays a single application with its jurisdiction filing statuses
 */
function FilingCard({
  application,
  onFileNow,
  onViewFiling,
  onEditRef,
  isHighlighted,
  onAssignToMe,
  onUnassign,
  onTransfer,
  onRecall,
  onAssignToStaff,
  canTransferToFirm,
  canRecallFromFirm,
  canReassign,
  currentAttorneyId,
  isAssigning,
  isUnassigning,
  isRecalling
}) {
  const { t } = useTranslation('attorney');
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    setIsDownloadingPdf(true);
    try {
      await downloadFilingPdf(application.id);
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Assignment info
  const assignment = application.assignment || {};
  const isAssignedToMe = assignment.attorneyId === currentAttorneyId;
  const isAssigned = assignment.isAssigned;
  const isTransferred = assignment.isTransferred;

  const pendingCount = application.filings?.filter(f => f.status === 'pending').length || 0;
  const filedCount = application.filings?.filter(f => f.status === 'filed').length || 0;
  const totalCount = application.filings?.length || 0;

  const getStatusIndicator = () => {
    if (pendingCount === 0) return { emoji: '🟢', text: t('attorney.filingCard.allFiled', 'All filed') };
    if (filedCount > 0) return { emoji: '🟡', text: t('attorney.filingCard.partiallyFiled', 'In progress') };
    return { emoji: '🟠', text: t('attorney.filingCard.pending', 'Pending') };
  };

  const status = getStatusIndicator();

  // Get classes from amended data first, then fall back to original
  const effectiveClassification = { ...(application.classification || {}), ...(application.amendments?.classification || {}) };
  const classNumbers = effectiveClassification.selectedClasses?.length > 0
    ? effectiveClassification.selectedClasses.map(cls => cls.classNumber || cls.number || cls.class).filter(Boolean)
    : effectiveClassification.niceClasses || [];

  return (
    <div className={`filing-card ${isHighlighted ? 'filing-card-highlighted' : ''}`}>
      <div className="filing-card-header">
        <div className="filing-card-title">
          <span className="filing-card-status-indicator">{status.emoji}</span>
          <div className="filing-card-info">
            <span className="filing-card-app-id">{application.applicationId}</span>
            <span className="filing-card-trademark-name">{application.trademark?.name || 'Unknown'}</span>
          </div>
        </div>
        <span className="filing-card-status-text">{status.text}</span>
        {application.amendmentStatus === 'pending_approval' && (
          <span className="filing-card-amendment-badge filing-card-amendment-pending">
            ⏳ Pending Approval
          </span>
        )}
        {application.amendmentStatus === 'query_received' && (
          <span className="filing-card-amendment-badge filing-card-amendment-query">
            ❓ Customer Query
          </span>
        )}
        {application.amendmentStatus === 'approved' && (
          <span className="filing-card-amendment-badge filing-card-amendment-approved">
            ✓ Changes Approved
          </span>
        )}
        {!application.amendmentStatus && application.amendmentHistory && application.amendmentHistory.length > 0 && (
          <span className="filing-card-amendment-badge">
            ✏ {application.amendmentHistory.length} {application.amendmentHistory.length === 1 ? 'change' : 'changes'}
          </span>
        )}
      </div>

      <div className="filing-card-meta">
        <span className="filing-card-client">
          {t('attorney.filingCard.client', 'Client')}: {application.clientName || 'Unknown'}
        </span>
        <span className="filing-card-classes">
          {t('attorney.filingCard.classes', 'Classes')}: {classNumbers.length > 0 ? classNumbers.join(', ') : '-'}
        </span>
      </div>

      {/* Assignment Info */}
      <div className="filing-card-assignment">
        {isTransferred && (
          <span className="assignment-badge assignment-transferred">
            {t('attorney.filingCard.transferredTo', 'Transferred to')}: {assignment.orgName}
          </span>
        )}
        {isAssigned ? (
          <span className={`assignment-badge ${isAssignedToMe ? 'assignment-mine' : 'assignment-other'}`}>
            {isAssignedToMe
              ? t('attorney.filingCard.assignedToYou', 'Assigned to you')
              : `${t('attorney.filingCard.assignedTo', 'Assigned to')}: ${assignment.attorneyName || 'Unknown'}`
            }
          </span>
        ) : (
          <span className="assignment-badge assignment-unassigned">
            {t('attorney.filingCard.unassigned', 'Unassigned')}
          </span>
        )}
      </div>

      <div className="filing-card-jurisdictions">
        <table className="jurisdiction-table">
          <thead>
            <tr>
              <th>{t('attorney.filingCard.country', 'Country')}</th>
              <th>{t('attorney.filingCard.status', 'Status')}</th>
              <th>{t('attorney.filingCard.ipOfficeRef', 'IP Office Ref')}</th>
              <th>{t('attorney.filingCard.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {application.filings?.map(filing => (
              <JurisdictionRow
                key={filing.jurisdiction}
                filing={filing}
                onFileNow={() => onFileNow(application, filing)}
                onView={() => onViewFiling(application, filing)}
                onEditRef={() => onEditRef(application, filing)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="filing-card-footer">
        <span className="filing-card-progress">
          {filedCount}/{totalCount} {t('attorney.filingCard.jurisdictionsFiled', 'jurisdictions filed')}
        </span>

        <div className="filing-card-actions">
          {/* Assign to Me button - show if unassigned */}
          {!isAssigned && (
            <button
              className="filing-action-btn assign-btn"
              onClick={() => onAssignToMe(application.id)}
              disabled={isAssigning}
            >
              {isAssigning
                ? t('attorney.filingCard.assigning', 'Assigning...')
                : t('attorney.filingCard.assignToMe', 'Assign to Me')
              }
            </button>
          )}

          {/* Unassign button - show if assigned to me and has permission */}
          {isAssignedToMe && canReassign && (
            <button
              className="filing-action-btn unassign-btn"
              onClick={() => onUnassign(application.id)}
              disabled={isUnassigning}
            >
              {isUnassigning
                ? t('attorney.filingCard.unassigning', 'Unassigning...')
                : t('attorney.filingCard.unassign', 'Unassign')
              }
            </button>
          )}

          {/* Transfer button - only for admin/attorneyadmin at internal firm */}
          {canTransferToFirm && !isTransferred && (
            <button
              className="filing-action-btn transfer-btn"
              onClick={() => onTransfer(application)}
            >
              {t('attorney.filingCard.transfer', 'Transfer to Firm')}
            </button>
          )}

          {/* Recall button - for admin/attorneyadmin to recall transferred apps */}
          {canRecallFromFirm && isTransferred && (
            <button
              className="filing-action-btn recall-btn"
              onClick={() => onRecall(application)}
              disabled={isRecalling}
            >
              {isRecalling
                ? t('attorney.filingCard.recalling', 'Recalling...')
                : t('attorney.filingCard.recall', 'Recall to MarkoTM')
              }
            </button>
          )}

          {/* Assign to Staff button - for org admins to assign to other attorneys */}
          {canReassign && onAssignToStaff && (
            <button
              className="filing-action-btn assign-staff-btn"
              onClick={() => onAssignToStaff(application)}
            >
              {t('attorney.filingCard.assignToStaff', 'Assign to Staff')}
            </button>
          )}

          {/* Download PDF summary */}
          <button
            className="filing-action-btn pdf-btn"
            onClick={handleDownloadPdf}
            disabled={isDownloadingPdf}
            title={t('attorney.filingCard.downloadPdf', 'Download Application Summary PDF')}
          >
            {isDownloadingPdf
              ? t('attorney.filingCard.downloading', 'Downloading...')
              : t('attorney.filingCard.downloadPdf', 'Download PDF')
            }
          </button>
        </div>
      </div>
    </div>
  );
}

export default FilingCard;
